import React, { FC } from "react";

export interface ChildModItem {
  id: number;
  name: string;
  price: number;
  onlinePrice: number;
  allowMultipleQuantity?: boolean;
  isAvailable?: number;
  orderBy?: number;
  hasChildModifierSets?: boolean;
}

export interface ChildModSet {
  id: number;
  name: string;
  noOfRequired: number;
  maximum: number;
  defaultPopup?: boolean;
  orderBy?: number;
  items: ChildModItem[];
}

export interface ChildBundle {
  fetched: ChildModSet[] | null;
  loading: boolean;
  error?: string;
  selections: Record<number, Record<number, boolean>>;
  children: Record<number, ChildBundle>;
}

interface Props {
  set: ChildModSet;
  selectedInSet: Record<number, boolean>;
  onToggle: (item: ChildModItem, checked: boolean) => void;
  renderChildrenAfter: (itemId: number) => React.ReactNode;
  showError: boolean;
  depth: number;
}

const NestedModifierSection: FC<Props> = ({
  set,
  selectedInSet,
  onToggle,
  renderChildrenAfter,
  showError,
  depth,
}) => {
  const selectedCount = Object.values(selectedInSet).filter(Boolean).length;
  const isRequired = set.noOfRequired > 0;
  const isSingleSelect = set.maximum === 1;
  const hasMinError = showError && selectedCount < set.noOfRequired;
  const hasMaxError =
    showError && set.maximum > 0 && selectedCount > set.maximum;

  const sortedItems = [...(set.items || [])].sort(
    (a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0)
  );

  return (
    <div
      data-nested-set-id={set.id}
      className={`py-2 mt-1 border-l-2 pl-3 ${
        depth === 1
          ? "border-purple-400 dark:border-purple-500"
          : "border-emerald-400 dark:border-emerald-500"
      } ${
        hasMinError || hasMaxError
          ? "bg-red-50/50 dark:bg-red-900/10 rounded-lg"
          : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1 px-2">
        <div className="flex items-center gap-2">
          <h5 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {set.name}
          </h5>
          {isRequired && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              Required
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {isRequired
            ? `Select ${set.noOfRequired}${
                set.maximum > set.noOfRequired ? ` - ${set.maximum}` : ""
              }`
            : `Up to ${set.maximum}`}
        </span>
      </div>

      {hasMinError && (
        <p className="text-xs text-red-500 dark:text-red-400 px-2 mb-1">
          Please select at least {set.noOfRequired} option
          {set.noOfRequired > 1 ? "s" : ""}.
        </p>
      )}
      {hasMaxError && (
        <p className="text-xs text-red-500 dark:text-red-400 px-2 mb-1">
          Maximum {set.maximum} selections allowed.
        </p>
      )}

      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg mx-2">
        {sortedItems.map((item, idx) => {
          const isChecked = !!selectedInSet[item.id];
          const isDisabled =
            !isSingleSelect &&
            !isChecked &&
            set.maximum > 0 &&
            selectedCount >= set.maximum;

          return (
            <React.Fragment key={item.id}>
              <label
                className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${
                  isDisabled
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-100 dark:hover:bg-slate-700/50"
                } ${
                  idx < sortedItems.length - 1
                    ? "border-b border-slate-100 dark:border-slate-700/50"
                    : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => {
                      if (!isDisabled) onToggle(item, !isChecked);
                    }}
                    className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-primary-500 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400 dark:bg-slate-700"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {item.name}
                  </span>
                </div>
                {item.onlinePrice > 0 && (
                  <span className="text-sm text-primary-600 dark:text-primary-400">
                    +${item.onlinePrice.toFixed(2)}
                  </span>
                )}
              </label>
              {isChecked && renderChildrenAfter(item.id)}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default NestedModifierSection;
